import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Optional,
  Output
} from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { DataTableBlock } from '@core/services/ai/ai-assistant.service';
import {
  inAppRouteOrNull,
  normalizeQueryParams,
  safeInAppRoute
} from '../safe-route.helper';
import { buildEntityDeeplink } from '../entity-deeplink.helper';

export interface DataTableRowAction {
  kind: 'remind' | 'remind-batch';
  tenantId?: string;
  contractId?: string;
  rowId?: string;
  targets?: { tenantId: string; contractId?: string }[];
}

@Component({
  selector: 'app-ai-data-table-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatButtonModule, MatTooltipModule, RouterLink],
  template: `
    <div class="card">
      @if (block.title || remindableRows().length > 1) {
        <header class="card__title">
          @if (block.title) {
            <span>{{ block.title }}</span>
          }
          @if (remindableRows().length > 1) {
            <button
              type="button"
              class="tbl__remind-all"
              (click)="emitRemindAll()">
              <mat-icon>notifications_active</mat-icon>
              {{ remindAllLabel() }}
            </button>
          }
        </header>
      }
      <div class="card__scroll">
        <table class="tbl">
          <thead>
            <tr>
              @for (col of block.columns; track col.key) {
                <th [attr.data-align]="col.align || 'left'">{{ col.label }}</th>
              }
              @if (hasRowActions()) {
                <th class="tbl__actions-h"></th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of block.rows; track row.id) {
              <tr [attr.data-tone]="row.tone || 'neutral'">
                @for (col of block.columns; track col.key) {
                  <td [attr.data-align]="col.align || 'left'">
                    {{ row.cells[col.key] }}
                  </td>
                }
                @if (hasRowActions()) {
                  <td class="tbl__actions">
                    @if (rowRoute(row); as route) {
                      <a
                        class="tbl__open"
                        [routerLink]="route"
                        [attr.aria-label]="openLabel()">
                        <mat-icon>open_in_new</mat-icon>
                      </a>
                    }
                    @if (row.rowAction === 'remind' && row.tenantId) {
                      <button
                        type="button"
                        class="tbl__remind"
                        [matTooltip]="remindLabel()"
                        (click)="emitRemind(row)">
                        <mat-icon>notification_important</mat-icon>
                      </button>
                    }
                  </td>
                }
              </tr>
            }
          </tbody>
          @if (block.totals; as totals) {
            <tfoot>
              <tr>
                @for (col of block.columns; track col.key) {
                  <td [attr.data-align]="col.align || 'left'">
                    {{ totals[col.key] }}
                  </td>
                }
                @if (hasRowActions()) {
                  <td></td>
                }
              </tr>
            </tfoot>
          }
        </table>
      </div>
      @if (block.caption) {
        <p class="card__caption">{{ block.caption }}</p>
      }
      @if (hasSeeAll()) {
        <a
          class="card__see-all"
          [routerLink]="safeSeeAllRoute()"
          [queryParams]="seeAllQueryParams()">
          {{ seeAllText() }}
          <mat-icon class="card__see-all-arrow">arrow_forward</mat-icon>
        </a>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .card {
        background: #fff;
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 12px;
        overflow: hidden;
      }
      .card__title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 10px 12px;
        font-weight: 600;
        font-size: 14px;
        color: rgba(15, 23, 42, 0.92);
        background: rgba(99, 102, 241, 0.04);
        border-bottom: 1px solid rgba(15, 23, 42, 0.06);
      }
      .tbl__remind-all {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        border: 1px solid rgba(15, 23, 42, 0.16);
        border-radius: 999px;
        background: #fff;
        padding: 4px 10px;
        font: inherit;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
      }
      .tbl__remind-all mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }
      .card__scroll {
        overflow-x: auto;
        /* A schedule can run to 60 rows. Left unbounded it pushed the rest of
           the conversation off-screen; capped, the header stays in view and the
           chat stays scrollable. */
        max-height: 360px;
        overflow-y: auto;
      }
      .tbl {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 13px;
      }
      .tbl th,
      .tbl td {
        padding: 8px 12px;
        border-bottom: 1px solid rgba(15, 23, 42, 0.06);
        /* Was top-aligned: with right-aligned amounts it read as if the numbers
           were floating above their row. */
        vertical-align: middle;
      }
      .tbl th {
        position: sticky;
        top: 0;
        z-index: 1;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(15, 23, 42, 0.55);
        /* Opaque, not translucent: rows scroll UNDER this now. */
        background: #f5f6f8;
      }
      .tbl td {
        color: rgba(15, 23, 42, 0.85);
      }
      .tbl [data-align='right'] {
        text-align: right;
        /* Equal-width digits so columns of money line up on the decimal. */
        font-variant-numeric: tabular-nums;
      }
      .tbl [data-align='center'] {
        text-align: center;
      }
      .tbl tbody tr:hover {
        background: rgba(99, 102, 241, 0.04);
      }
      .tbl tbody tr[data-tone='warning'] {
        background: rgba(245, 158, 11, 0.06);
      }
      .tbl tbody tr[data-tone='danger'] {
        background: rgba(239, 68, 68, 0.06);
      }
      .tbl tbody tr[data-tone='positive'] {
        background: rgba(16, 185, 129, 0.05);
      }
      .tbl tfoot td {
        font-weight: 700;
        background: rgba(15, 23, 42, 0.03);
        border-top: 1px solid rgba(15, 23, 42, 0.1);
        border-bottom: none;
      }
      .tbl__actions,
      .tbl__actions-h {
        width: 1%;
        white-space: nowrap;
        text-align: right;
      }
      .tbl__open,
      .tbl__remind {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: transparent;
        color: #4f46e5;
        cursor: pointer;
        padding: 2px;
        text-decoration: none;
        vertical-align: middle;
      }
      .tbl__open mat-icon,
      .tbl__remind mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
      .card__caption {
        margin: 0;
        padding: 6px 12px 10px;
        font-size: 12px;
        color: rgba(15, 23, 42, 0.55);
        font-style: italic;
      }
      .card__see-all {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        margin: 0 12px 10px;
        font-size: 13px;
        font-weight: 600;
        color: #4f46e5;
        text-decoration: none;
      }
      .card__see-all:hover {
        text-decoration: underline;
      }
      .card__see-all-arrow {
        font-size: 15px;
        width: 15px;
        height: 15px;
      }

      /* Dark theme. Tailwind runs in class mode (see darkMode in
         tailwind.config.ts) and these styles are component-scoped, so the
         ancestor .dark class has to be reached with :host-context. Without this
         the whole block stayed white on a dark shell. */
      :host-context(.dark) .card {
        background: #1e293b;
        border-color: rgba(148, 163, 184, 0.18);
      }
      :host-context(.dark) .card__title {
        color: rgba(241, 245, 249, 0.92);
        background: rgba(99, 102, 241, 0.12);
        border-bottom-color: rgba(148, 163, 184, 0.14);
      }
      :host-context(.dark) .tbl th {
        color: rgba(226, 232, 240, 0.62);
        background: #273449;
      }
      :host-context(.dark) .tbl th,
      :host-context(.dark) .tbl td {
        border-bottom-color: rgba(148, 163, 184, 0.12);
      }
      :host-context(.dark) .tbl td {
        color: rgba(226, 232, 240, 0.88);
      }
      :host-context(.dark) .tbl tbody tr:hover {
        background: rgba(99, 102, 241, 0.14);
      }
      :host-context(.dark) .tbl tbody tr[data-tone='warning'] {
        background: rgba(245, 158, 11, 0.14);
      }
      :host-context(.dark) .tbl tbody tr[data-tone='danger'] {
        background: rgba(239, 68, 68, 0.14);
      }
      :host-context(.dark) .tbl tbody tr[data-tone='positive'] {
        background: rgba(16, 185, 129, 0.13);
      }
      :host-context(.dark) .tbl tfoot td {
        background: rgba(148, 163, 184, 0.1);
        border-top-color: rgba(148, 163, 184, 0.22);
      }
      :host-context(.dark) .card__caption {
        color: rgba(226, 232, 240, 0.6);
      }
      :host-context(.dark) .card__see-all {
        color: #a5b4fc;
      }
    `
  ]
})
export class AiDataTableBlockComponent {
  @Input({ required: true }) block!: DataTableBlock;
  @Output() readonly rowAction = new EventEmitter<DataTableRowAction>();

  constructor(
    @Optional() private readonly translate: TranslateService | null = null
  ) {}

  hasSeeAll(): boolean {
    const route = this.block?.seeAllRoute;
    return typeof route === 'string' && route.length > 0;
  }

  hasRowActions(): boolean {
    return (this.block?.rows ?? []).some(
      (r) => !!this.rowRoute(r) || (r.rowAction === 'remind' && !!r.tenantId)
    );
  }

  rowRoute(
    row: DataTableBlock['rows'][number]
  ): string | null {
    const explicit = inAppRouteOrNull(row.route);
    if (explicit) return explicit;
    if (row.entityType && row.id) {
      return buildEntityDeeplink(row.entityType, row.id);
    }
    return null;
  }

  emitRemind(row: DataTableBlock['rows'][number]): void {
    if (!row.tenantId) return;
    this.rowAction.emit({
      kind: 'remind',
      tenantId: row.tenantId,
      contractId: row.contractId,
      rowId: row.id
    });
  }

  remindableRows(): { tenantId: string; contractId?: string }[] {
    return (this.block?.rows ?? [])
      .filter((r) => r.rowAction === 'remind' && !!r.tenantId)
      .slice(0, 10)
      .map((r) => ({
        tenantId: r.tenantId as string,
        contractId: r.contractId
      }));
  }

  emitRemindAll(): void {
    const targets = this.remindableRows();
    if (targets.length < 2) return;
    this.rowAction.emit({ kind: 'remind-batch', targets });
  }

  openLabel(): string {
    return this.resolve('aiAssistant.openEntity', 'Open');
  }

  remindLabel(): string {
    return this.resolve('aiAssistant.remind', 'Send reminder');
  }

  remindAllLabel(): string {
    return this.resolve('aiAssistant.remindAll', 'Remind all');
  }

  safeSeeAllRoute(): string {
    return safeInAppRoute(this.block?.seeAllRoute);
  }

  seeAllQueryParams(): Record<string, string> | null {
    return normalizeQueryParams(this.block?.seeAllQueryParams);
  }

  seeAllText(): string {
    const key = this.block?.seeAllLabelKey ?? 'aiAssistant.list.seeAll';
    return this.resolve(key, 'See all');
  }

  private resolve(key: string, fallback: string): string {
    if (!this.translate) return fallback;
    const translated = this.translate.instant(key);
    return typeof translated === 'string' && translated !== key
      ? translated
      : fallback;
  }
}
